import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { Download, History } from 'lucide-react';
import type { AuditRow } from '@runik/shared';
import { PageHeader } from '../../../../components/layout/PageHeader';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../../../components/ui/sheet';
import { ScrollArea } from '../../../../components/ui/scroll-area';
import { DataTable } from '../../../../components/data/DataTable';
import { EmptyState } from '../../../../components/data/EmptyState';
import { auditKey, exportAuditCsvUrl, listAudit } from '../../../../lib/queries/audit';

function AuditPage() {
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<AuditRow | null>(null);

  const audit = useQuery({
    queryKey: auditKey({ page, limit: 25 }),
    queryFn: () => listAudit({ page, limit: 25 }),
  });

  const columns = useMemo<ColumnDef<AuditRow, unknown>[]>(
    () => [
      {
        accessorKey: 'ts',
        header: 'Zaman',
        cell: ({ row }) => format(new Date(row.original.ts), 'd MMM HH:mm:ss', { locale: tr }),
      },
      {
        accessorKey: 'actorEmail',
        header: 'Aktör',
        cell: ({ row }) => row.original.actorEmail ?? <span className="text-slate-400">sistem</span>,
      },
      {
        accessorKey: 'action',
        header: 'Aksiyon',
        cell: ({ row }) => <Badge variant="outline">{row.original.action}</Badge>,
      },
      {
        accessorKey: 'target',
        header: 'Hedef',
        cell: ({ row }) =>
          row.original.target ? (
            <code className="text-xs text-slate-600">{row.original.target.slice(0, 8)}…</code>
          ) : (
            <span className="text-slate-400">—</span>
          ),
      },
      { accessorKey: 'ip', header: 'IP', cell: ({ row }) => row.original.ip ?? '—' },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <Button variant="ghost" size="sm" onClick={() => setSelected(row.original)}>
            Detay
          </Button>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <PageHeader
        title="Denetim Kaydı"
        description="Sistem aktivite kayıtları"
        actions={
          <Button asChild variant="outline">
            <a href={exportAuditCsvUrl({})} target="_blank" rel="noreferrer">
              <Download className="h-4 w-4" /> CSV Dışa Aktar
            </a>
          </Button>
        }
      />
      <div className="p-6">
        <DataTable
          columns={columns}
          data={audit.data?.data}
          loading={audit.isLoading}
          getRowId={(row) => row.id}
          empty={
            <EmptyState
              icon={History}
              title="Henüz kayıt yok"
              description="Sistem etkinlikleri burada listelenecek."
            />
          }
          pagination={
            audit.data
              ? {
                  page: audit.data.page,
                  limit: audit.data.limit,
                  total: audit.data.total,
                  onPageChange: setPage,
                }
              : undefined
          }
        />
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>Denetim Detayı</SheetTitle>
          </SheetHeader>
          {selected && (
            <ScrollArea className="mt-4 h-[calc(100vh-7rem)]">
              <pre className="text-xs bg-slate-50 p-4 rounded-md overflow-x-auto">
                {JSON.stringify(selected, null, 2)}
              </pre>
            </ScrollArea>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}

export const Route = createFileRoute('/_app/admin/audit/')({ component: AuditPage });
